import React from 'react';

/**
 * BFI Groupe — Banking style logo.
 *
 * Design rationale:
 *  - Rounded shield silhouette = trust / security (banking).
 *  - Blue → indigo gradient = corporate finance feel, premium.
 *  - Gold accent bar = value / precious assets.
 *  - Stylised upward arrow inside the shield = growth, performance, ascending markets.
 *  - "BFI" wordmark in bold, "GROUPE" subtitle in spaced caps (luxury / institutional).
 *  - Two variants:
 *      <Logo />       → for light backgrounds (dark text + colored shield)
 *      <LogoLight />  → for dark backgrounds  (white text + monochrome white shield)
 *
 *  Both variants share the SAME proportions (40×40 viewport for the shield,
 *  same wordmark layout) so they are interchangeable.
 */

function ShieldMark({ size = 40, mono = false }) {
  const uid = React.useId();
  const gradId = `bfi-grad-${uid}`;
  const shineId = `bfi-shine-${uid}`;

  // Colours
  const blueDeep = '#1E3A8A';      // primary deep blue
  const indigo = '#3730A3';        // indigo blend
  const gold = '#F5B544';          // accent
  const white = '#ffffff';

  if (mono) {
    // White-line variant for dark backgrounds
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 64 64"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        {/* Shield outline */}
        <path
          d="M32 4 L56 12 L56 32 C56 46.5 45.6 56.4 32 60 C18.4 56.4 8 46.5 8 32 L8 12 Z"
          fill="rgba(255,255,255,0.06)"
          stroke={white}
          strokeWidth="2.25"
          strokeLinejoin="round"
        />
        {/* Accent bar */}
        <rect x="16" y="16" width="32" height="2.5" rx="1.25" fill={white} opacity="0.85" />
        {/* Upward arrow / growth */}
        <path
          d="M20 44 L32 26 L44 44"
          stroke={white}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        {/* Arrow head accent */}
        <path
          d="M28 30 L32 26 L36 30"
          stroke={white}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          opacity="0.7"
        />
      </svg>
    );
  }

  // Coloured variant for light backgrounds
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="64" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor={blueDeep} />
          <stop offset="1" stopColor={indigo} />
        </linearGradient>
        <linearGradient id={shineId} x1="0" y1="0" x2="64" y2="0" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.18" />
          <stop offset="0.5" stopColor="#ffffff" stopOpacity="0.04" />
          <stop offset="1" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Shield body */}
      <path
        d="M32 4 L56 12 L56 32 C56 46.5 45.6 56.4 32 60 C18.4 56.4 8 46.5 8 32 L8 12 Z"
        fill={`url(#${gradId})`}
      />
      {/* Subtle gloss */}
      <path
        d="M32 4 L56 12 L56 32 C56 46.5 45.6 56.4 32 60 C18.4 56.4 8 46.5 8 32 L8 12 Z"
        fill={`url(#${shineId})`}
      />
      {/* Gold accent bar at the top of the shield */}
      <rect x="16" y="16" width="32" height="2.5" rx="1.25" fill={gold} />

      {/* Upward arrow / growth mark */}
      <path
        d="M20 44 L32 26 L44 44"
        stroke={white}
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Inner accent stroke */}
      <path
        d="M26 44 L32 35 L38 44"
        stroke={gold}
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

export function Logo({ className = '', size = 40, withText = true }) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <ShieldMark size={size} />
      {withText && (
        <div className="leading-tight select-none">
          <div className="font-extrabold text-xl tracking-tight text-[#1E3A8A] dark:text-blue-200">
            BFI
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500 dark:text-slate-400 -mt-0.5">
            Groupe
          </div>
        </div>
      )}
    </div>
  );
}

export function LogoLight({ className = '', size = 40, withText = true }) {
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <ShieldMark size={size} mono />
      {withText && (
        <div className="leading-tight select-none">
          <div className="font-extrabold text-xl tracking-tight text-white">BFI</div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-white/70 -mt-0.5">
            Groupe
          </div>
        </div>
      )}
    </div>
  );
}

export default Logo;
